import React, { useEffect, useState, useCallback } from 'react';
import { getHistory } from '../api/gameApi';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--space-xl)',
    flexWrap: 'wrap',
    gap: 'var(--space-md)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.75rem',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: 0,
    letterSpacing: '0.03em',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--color-text-muted)',
    margin: '4px 0 0',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--font-body)',
    fontSize: '0.9rem',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontFamily: 'var(--font-display)',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.03)',
    color: 'var(--color-text-secondary)',
  },
  row: {
    transition: 'background-color var(--transition-fast)',
  },
  empty: {
    textAlign: 'center',
    padding: 'var(--space-2xl)',
    color: 'var(--color-text-muted)',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: 'var(--space-2xl)',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'var(--space-lg)',
    flexWrap: 'wrap',
    gap: 'var(--space-sm)',
  },
  pageInfo: {
    fontSize: '0.85rem',
    color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-display)',
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
  },
  pageBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  pageBtnDisabled: {
    opacity: 0.3,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  pageBtnActive: {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-bg-primary)',
    borderColor: 'var(--color-primary)',
  },
  pageNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    fontSize: '0.85rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 500,
  },
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(value) {
  if (value == null) return '-';
  return `\u20AC ${Number(value).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function AdminGames() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadGames = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const data = await getHistory({ page: pageNum });
      // Laravel paginated response
      if (data.data) {
        setGames(data.data);
        setPage(data.current_page || pageNum);
        setLastPage(data.last_page || 1);
        setTotal(data.total || data.data.length);
      } else if (Array.isArray(data)) {
        setGames(data);
        setLastPage(1);
        setTotal(data.length);
      } else {
        setGames(data.games || []);
        setLastPage(data.last_page || 1);
        setTotal(data.total || 0);
      }
    } catch {
      // handle silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGames(page);
  }, [page, loadGames]);

  const goToPage = (p) => {
    if (p >= 1 && p <= lastPage) {
      setPage(p);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(lastPage, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          style={{
            ...styles.pageNumber,
            ...(i === page ? styles.pageBtnActive : {}),
          }}
          onClick={() => goToPage(i)}
          onMouseEnter={(e) => {
            if (i !== page) {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.color = 'var(--color-primary)';
            }
          }}
          onMouseLeave={(e) => {
            if (i !== page) {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }
          }}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Storico Partite</h1>
          <p style={styles.subtitle}>Tutte le partite completate</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => loadGames(page)}>
          <RefreshCw size={14} />
          Aggiorna
        </Button>
      </div>

      {loading ? (
        <div style={styles.centered}>
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <>
          <div style={styles.tableWrapper}>
            {games.length === 0 ? (
              <div style={styles.empty}>
                <p style={{ margin: 0 }}>Nessuna partita trovata</p>
              </div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Codice</th>
                    <th style={styles.th}>Giocatore</th>
                    <th style={styles.th}>Stato</th>
                    <th style={styles.th}>Premio Finale</th>
                    <th style={styles.th}>Offerta</th>
                    <th style={styles.th}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((game) => (
                    <tr
                      key={game.id}
                      style={styles.row}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-card)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td style={{ ...styles.td, fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
                        {game.code || `#${game.id}`}
                      </td>
                      <td style={{ ...styles.td, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                        {game.player_name || '-'}
                      </td>
                      <td style={styles.td}>
                        <Badge variant="success">Completata</Badge>
                      </td>
                      <td style={{ ...styles.td, fontFamily: 'var(--font-mono)' }}>
                        {formatCurrency(game.final_prize_value)}
                      </td>
                      <td style={styles.td}>
                        {game.offer_accepted != null ? (
                          <Badge variant={game.offer_accepted ? 'info' : 'warning'}>
                            {game.offer_accepted ? 'Accettata' : 'Rifiutata'}
                          </Badge>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                        )}
                      </td>
                      <td style={{ ...styles.td, fontSize: '0.8rem' }}>
                        {formatDate(game.completed_at || game.started_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {lastPage > 1 && (
            <div style={styles.pagination}>
              <span style={styles.pageInfo}>
                Pagina {page} di {lastPage} ({total} partite totali)
              </span>
              <div style={styles.pageControls}>
                <button
                  style={{
                    ...styles.pageBtn,
                    ...(page <= 1 ? styles.pageBtnDisabled : {}),
                  }}
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft size={16} />
                </button>
                {renderPageNumbers()}
                <button
                  style={{
                    ...styles.pageBtn,
                    ...(page >= lastPage ? styles.pageBtnDisabled : {}),
                  }}
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= lastPage}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
